import { graphql } from "../../graphql";

export const CurvePackageFragment = graphql(`
	fragment CurvePackageFragment on package {
		id
		name
		package_type
		permissions {
			permission
		}
		folio_users {
			id
		}
	}
`);

export const CurvePackageQuery = graphql(`
	query CurvePackage {
		package {
			...CurvePackageFragment
		}
	}
`);

// Products where artis_type is "eod" or "curve_type" is "snapshot".
export const CurveEODProductsQuery = graphql(`
	query CurveEODProducts($folioUser: String!) {
		product(where: {artis_type: {_eq: eod}, _or: {curve_type: {_eq: snapshot}}}) {
			...ProductInfoFragment
		}
	}
`);

// Global products.
export const GlobalCurvesQuery = graphql(`
	query GlobalCurves($folioUser: String!) {
		global_product {
			...GlobalProductInfoFragment
		}
	}
`);

export const CurvePackagesFragment = graphql(`
	fragment CurvePackagesFragment on package {
		id
		name
		package_type
	}
`);

export const CurvePackagesQuery = graphql(`
	query CurvePackages {
		package {
			...CurvePackagesFragment
		}
	}
`);

export const CurveUOMFragment = graphql(`
	fragment CurveUOMFragment on product_uom {
		value
		description
	}
`);

export const CurveUOMQuery = graphql(`
	query CurveUOM {
		product_uom {
			...CurveUOMFragment
		}
	}
`);

export const CurveProductTypeFragment = graphql(`
	fragment CurveProductTypeFragment on product_type {
		value
	}
`);

export const CurveProductTypeQuery = graphql(`
	query CurveProductType {
		product_type {
			...CurveProductTypeFragment
		}
	}
`);

export const CurveCommodityParentGroupFragment = graphql(`
	fragment CurveCommodityParentGroupFragment on commodity_parent_group {
		id
		value
	}
`);

export const CurveCommodityParentGroupsQuery = graphql(`
	query CurveCommodityParentGroups {
		commodity_parent_group {
			...CurveCommodityParentGroupFragment
		}
	}
`);

export const CurveRegionsFragment = graphql(`
	fragment CurveRegionsFragment on geographical_region {
		id
		value
	}
`);

export const CurveRegionsQuery = graphql(`
	query CurveRegions {
		geographical_region {
			...CurveRegionsFragment
		}
	}
`);

export const CurveCommodityGroupFragment = graphql(`
	fragment CurveCommodityGroupFragment on commodity_group {
		id
		value
		parent
	}
`);

export const CurveCommodityGroupsQuery = graphql(`
	query CurveCommodityGroups {
		commodity_group {
			...CurveCommodityGroupFragment
		}
	}
`);

export const CurveUpsertMutation = graphql(`
	mutation CurveUpsertMutation($products: [product_insert_input!]!, $product_configs: [product_config_insert_input!]!, $offsets_to_delete: [Int!]!, $product_id: uuid!) {
	insert_product(objects: $products, on_conflict: {constraint: product_pkey, update_columns: [name, description, code, maturity, summary_type, uom, geographical_region, commodity_group, eod_product_dep, package, curve_type, product_type]}) {
		returning {
			id
		}
	}
	delete_product_config(where: {product: {_eq: $product_id}, relative_month: {_in: $offsets_to_delete}}) {
		returning {
			id
		}
	}
	insert_product_config(objects: $product_configs, on_conflict: {constraint: product_config_pkey, update_columns: [formula]}) {
		returning {
			id
		}
	}
	}
`);

export const CurveGlobalDependenciesQuery = graphql(`
	query CurveGlobalDependencies($product: uuid!) {
		product_global_dependencies(where: {product: {_eq: $product}}) {
			global_product
			product
		}
	}
`);

export const CurveGlobalInsertMutation = graphql(`
	mutation CurveGlobalInsertMutation($objects: [product_global_dependencies_insert_input!]!) {
    insert_product_global_dependencies(objects: $objects) {
      affected_rows
    }
  }
`);

export const CurveGlobalDeleteMutation = graphql(`
	mutation CurveGlobalDeleteMutation($global_products: [uuid!], $product: uuid) {
    delete_product_global_dependencies(where: {global_product: {_in: $global_products}, product: {_eq: $product}}){
      returning {
        product
        global_product
      }
    }
  }
`);

// Which packages currently have a live global mirror (per WEB-1563).
export const GlobalPackagesByIdsQuery = graphql(`
	query GlobalPackagesByIds($ids: [Int!]!) {
		global_package(where: { id: { _in: $ids } }) {
			id
		}
	}
`);

// Visible `global_package` rows (filtered by global_permissions). Kept for
// mirror sync / debug — non-manager UI gating uses `package.is_global` instead.
export const AllGlobalisedPackageIdsQuery = graphql(`
	query AllGlobalisedPackageIds {
		global_package {
			id
		}
	}
`);

// Source of truth for Curve Management grey-outs / manager warnings: packages
// the user can already read that are flagged globalised in UMI.
export const GlobalisedPackageFlagsQuery = graphql(`
	query GlobalisedPackageFlags {
		package(where: { is_global: { _eq: true } }) {
			id
		}
	}
`);

// Existing global mirrors for these curves (self-mirrors share the curve id).
export const GlobalCurveMirrorsQuery = graphql(`
	query GlobalCurveMirrors($ids: [uuid!]!) {
		global_product(where: { id: { _in: $ids } }) {
			id
			global_package
		}
	}
`);

// Upsert a curve (and its configs) into the corresponding global package.
// Used when creating/editing a curve in a globalised package, or moving one
// into a globalised package.
export const UpsertGlobalCurvesMutation = graphql(`
	mutation UpsertGlobalCurves(
		$globalProducts: [global_product_insert_input!]!
		$globalProductConfigs: [global_product_config_insert_input!]!
		$offsetsToDelete: [Int!]!
		$productIds: [uuid!]!
	) {
		insert_global_product(
			objects: $globalProducts
			on_conflict: {
				constraint: global_product_pkey
				update_columns: [
					name
					description
					uom
					geographical_region
					commodity_group
					curve_type
					global_package
				]
			}
		) {
			affected_rows
		}
		delete_global_product_config(
			where: { product: { _in: $productIds }, relative_month: { _in: $offsetsToDelete } }
		) {
			affected_rows
		}
		insert_global_product_config(
			objects: $globalProductConfigs
			on_conflict: {
				constraint: global_product_config_pkey
				update_columns: [formula, relative_month]
			}
		) {
			affected_rows
		}
	}
`);

// Self-link a product to its own global_product row (same id). Matches the
// WEB-1563 package-globalise flow. on_conflict so re-saves are idempotent.
export const InsertGlobalCurveSelfDepsMutation = graphql(`
	mutation InsertGlobalCurveSelfDeps($objects: [product_global_dependencies_insert_input!]!) {
		insert_product_global_dependencies(
			objects: $objects
			on_conflict: { constraint: product_global_dependencies_pkey, update_columns: [] }
		) {
			affected_rows
		}
	}
`);

// Remove a curve's global mirror when it leaves a globalised package.
// Note: do not include `delete_global_instrument` here — that field is not
// exposed on mutation_root for the webapp role (admin unglobalise uses it).
export const RemoveGlobalCurvesMutation = graphql(`
	mutation RemoveGlobalCurves($globalProductIds: [uuid!]!) {
		delete_global_product_config(where: { product: { _in: $globalProductIds } }) {
			affected_rows
		}
		delete_product_global_dependencies(where: { global_product: { _in: $globalProductIds } }) {
			affected_rows
		}
		delete_global_product(where: { id: { _in: $globalProductIds } }) {
			affected_rows
		}
	}
`);
